// #ux-redesign-start: useResponsive hook (Paso 2 - Primitivas DS)
// Hook reactivo a breakpoints. Reemplaza los `window.innerWidth < 992` inline
// que aparecen hardcoded en Admin.jsx y varios componentes.
//
// Breakpoints alineados con Bootstrap 4 (compatibilidad con Reactstrap):
//   sm:  576px
//   md:  768px
//   lg:  992px
//   xl:  1200px
//
// PORTABILIDAD: idéntico para Web-Patients.
//
// USO:
//   const { isMobile, isTablet, isDesktop, width, bp } = useResponsive();
//   // isMobile: width < 768 (sm o menor)
//   // isTablet: 768 <= width < 992 (md)
//   // isDesktop: width >= 992 (lg o mayor)
//   // bp: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react';

export const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
};

const getBreakpoint = (w) => {
  if (w >= BREAKPOINTS.xl) return 'xl';
  if (w >= BREAKPOINTS.lg) return 'lg';
  if (w >= BREAKPOINTS.md) return 'md';
  if (w >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

const getInitialWidth = () => {
  if (typeof window === 'undefined') return BREAKPOINTS.lg;
  return window.innerWidth;
};

const useResponsive = () => {
  const [width, setWidth] = useState(getInitialWidth);

  const handleResize = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', handleResize);
    // Llamar una vez para sincronizar (en caso de SSR/hydration mismatch)
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const bp = getBreakpoint(width);

  return {
    width,
    bp,
    isMobile:  width < BREAKPOINTS.md,
    isTablet:  width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,
    isXl:      width >= BREAKPOINTS.xl,
  };
};

export default useResponsive;

// #ux-redesign: inyecta botones de carousel (‹ ›) a izq/der del elemento.
// Los botones aparecen/desaparecen segun haya contenido scroll en esa direccion.
// Idempotente — si ya se inyectaron no los duplica.
export const injectCarouselControls = (selector) => {
  if (typeof document === 'undefined') return;
  const elements = document.querySelectorAll(selector);

  elements.forEach((el) => {
    // Evitar doble inyeccion
    if (el._carouselWrapped) return;

    // Wrappear el elemento en un container con position relative
    const parent = el.parentNode;
    if (!parent) return;

    const wrap = document.createElement('div');
    wrap.className = 'ux-carousel-wrap';
    parent.insertBefore(wrap, el);
    wrap.appendChild(el);

    // Botones izq/der
    const makeBtn = (dir, symbol) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `ux-carousel-btn ux-carousel-btn--${dir}`;
      btn.setAttribute('aria-label', dir === 'left' ? 'Vorherige' : 'Nächste');
      btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="${dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'}"/></svg>`;
      btn.addEventListener('click', () => {
        const amount = el.clientWidth * 0.7;
        el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
      });
      return btn;
    };

    const leftBtn = makeBtn('left', '‹');
    const rightBtn = makeBtn('right', '›');
    wrap.appendChild(leftBtn);
    wrap.appendChild(rightBtn);

    // Actualizar visibilidad de los botones segun scroll
    const updateBtns = () => {
      const atStart = el.scrollLeft <= 4;
      const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 4;
      leftBtn.classList.toggle('is-hidden', atStart);
      rightBtn.classList.toggle('is-hidden', atEnd);
    };
    el.addEventListener('scroll', updateBtns, { passive: true });
    window.addEventListener('resize', updateBtns);
    setTimeout(updateBtns, 50);

    el._carouselWrapped = true;
  });
};

// #ux-redesign: mini helper para habilitar drag-to-scroll horizontal con mouse
// en cualquier elemento. Se invoca al click en el .chat_list u otros carouseles.
// Para activarlo en una pantalla: useEffect(() => enableDragScroll('.chat_list'), [])
// (se autoinicia desde Sidebar.jsx para chat_list global)
export const enableDragScroll = (selector) => {
  if (typeof document === 'undefined') return () => {};
  const elements = document.querySelectorAll(selector);
  const cleanups = [];

  elements.forEach((el) => {
    if (el._dragScrollAttached) return;
    el._dragScrollAttached = true;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const onDown = (e) => {
      isDown = true;
      el.classList.add('is-grabbing');
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };
    const onLeave = () => { isDown = false; el.classList.remove('is-grabbing'); };
    const onUp = () => { isDown = false; el.classList.remove('is-grabbing'); };
    const onMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5;
      el.scrollLeft = scrollLeft - walk;
    };

    el.addEventListener('mousedown', onDown);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('mouseup', onUp);
    el.addEventListener('mousemove', onMove);

    cleanups.push(() => {
      el._dragScrollAttached = false;
      el.removeEventListener('mousedown', onDown);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('mouseup', onUp);
      el.removeEventListener('mousemove', onMove);
    });
  });

  return () => cleanups.forEach((c) => c());
};
// #ux-redesign-end
